import { ref, watch, onBeforeUnmount, toValue, nextTick } from 'vue'
import {
    computePosition,
    autoUpdate,
    flip,
    shift,
    offset,
    size,
} from '@floating-ui/dom'

/**
 * Viewport-aware positioning + open state for dropdown/context menus.
 * Mirrors the floating-ui setup already used by Tooltip.vue, but adds
 * open/close state, outside-click, and Escape handling for menus.
 *
 * `placement`, `offsetPx`, and `matchWidth` accept either plain values or
 * refs/getters (anything `toValue` unwraps), so a consumer can change them
 * reactively and have the next open (or the live autoUpdate loop) pick up
 * the new value.
 */
export function useFloatingMenu({
    placement = 'bottom-end',
    offsetPx = 8,
    matchWidth = false,
} = {}) {
    const referenceRef = ref(null)
    const floatingRef = ref(null)
    const isOpen = ref(false)
    // Hidden until the first computePosition resolves, so the menu doesn't
    // flash at (0,0) for the one frame before its real position is known.
    const floatingStyles = ref({
        position: 'fixed',
        top: '0px',
        left: '0px',
        visibility: 'hidden',
    })

    let stopAutoUpdate = null

    function buildMiddleware() {
        const middleware = [
            offset(toValue(offsetPx)),
            flip(),
            shift({ padding: 8 }),
        ]
        if (toValue(matchWidth)) {
            middleware.push(
                size({
                    apply({ rects, elements }) {
                        elements.floating.style.width = `${rects.reference.width}px`
                    },
                })
            )
        }
        return middleware
    }

    async function updatePosition() {
        if (!referenceRef.value || !floatingRef.value) return
        const { x, y } = await computePosition(
            referenceRef.value,
            floatingRef.value,
            {
                strategy: 'fixed',
                placement: toValue(placement),
                middleware: buildMiddleware(),
            }
        )
        floatingStyles.value = {
            position: 'fixed',
            top: `${y}px`,
            left: `${x}px`,
        }
    }

    function handleOutsideClick(event) {
        if (!isOpen.value) return
        if (referenceRef.value?.contains(event.target)) return
        if (floatingRef.value?.contains(event.target)) return
        close()
    }

    function isVisible(el) {
        return !!(
            el.offsetWidth ||
            el.offsetHeight ||
            el.getClientRects().length
        )
    }

    function getMenuItems() {
        if (!floatingRef.value) return []
        return Array.from(
            floatingRef.value.querySelectorAll('[role="menuitem"]')
        ).filter((el) => !el.disabled && isVisible(el))
    }

    function focusItemAt(items, index) {
        if (!items.length) return
        const normalized =
            ((index % items.length) + items.length) % items.length
        items.at(normalized)?.focus()
    }

    // Roving focus among role="menuitem" descendants, per the WAI-ARIA
    // menu-button pattern. Items are unreachable by Tab (tabindex="-1"),
    // so Arrow/Home/End are the only way in or around once the menu is open.
    function handleKeydown(event) {
        if (event.key === 'Escape') {
            close()
            return
        }

        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            return
        }

        // Only handle navigation keys while focus is actually on the
        // trigger or inside the menu — otherwise this global listener would
        // hijack Home/End/Arrow keys from unrelated page content (e.g. text
        // inputs) for as long as the menu happens to be open.
        const activeElement = document.activeElement
        const focusIsInMenu =
            floatingRef.value?.contains(activeElement) ||
            activeElement === referenceRef.value
        if (!focusIsInMenu) return

        const items = getMenuItems()
        if (!items.length) return

        const currentIndex = items.indexOf(activeElement)
        event.preventDefault()

        if (event.key === 'ArrowDown') {
            focusItemAt(items, currentIndex === -1 ? 0 : currentIndex + 1)
        } else if (event.key === 'ArrowUp') {
            focusItemAt(items, currentIndex === -1 ? -1 : currentIndex - 1)
        } else if (event.key === 'Home') {
            focusItemAt(items, 0)
        } else if (event.key === 'End') {
            focusItemAt(items, -1)
        }
    }

    function open() {
        isOpen.value = true
    }

    // Focus is restored to the trigger only if it was still inside the menu
    // at close time — an outside click that already moved focus elsewhere
    // (native mousedown-before-click focus) is left alone rather than
    // yanked back.
    function close() {
        const shouldRestoreFocus =
            !!floatingRef.value &&
            floatingRef.value.contains(document.activeElement)

        isOpen.value = false

        if (
            shouldRestoreFocus &&
            referenceRef.value &&
            document.body.contains(referenceRef.value)
        ) {
            referenceRef.value.focus()
        }
    }

    function toggle() {
        isOpen.value ? close() : open()
    }

    // Listeners (and autoUpdate) only run while the menu is actually open, so
    // rendering many closed menu instances (e.g. one per table row) doesn't
    // leave a pile of active document listeners behind.
    //
    // `onCleanup` guards against a close (or unmount) racing the in-flight
    // `updatePosition()` from a previous open: without it, a stale open call
    // could still start `autoUpdate` after the menu had already been closed,
    // leaving a positioning loop running for a menu that's no longer open.
    watch(
        isOpen,
        (open, _prevOpen, onCleanup) => {
            if (!open) {
                document.removeEventListener('click', handleOutsideClick, true)
                document.removeEventListener('keydown', handleKeydown)
                stopAutoUpdate?.()
                stopAutoUpdate = null
                return
            }

            floatingStyles.value = {
                position: 'fixed',
                top: '0px',
                left: '0px',
                visibility: 'hidden',
            }

            let cancelled = false
            onCleanup(() => {
                cancelled = true
            })

            document.addEventListener('click', handleOutsideClick, true)
            document.addEventListener('keydown', handleKeydown)

            updatePosition().then(async () => {
                if (cancelled) return
                if (referenceRef.value && floatingRef.value) {
                    stopAutoUpdate = autoUpdate(
                        referenceRef.value,
                        floatingRef.value,
                        updatePosition
                    )
                }
                // Wait for Vue to actually patch the DOM with the
                // visibility-restoring style set above — focusing a
                // menuitem while its ancestor still renders
                // visibility:hidden is silently a no-op.
                await nextTick()
                if (cancelled) return
                getMenuItems()[0]?.focus()
            })
        },
        { flush: 'post' }
    )

    onBeforeUnmount(() => {
        document.removeEventListener('click', handleOutsideClick, true)
        document.removeEventListener('keydown', handleKeydown)
        stopAutoUpdate?.()
    })

    return {
        referenceRef,
        floatingRef,
        isOpen,
        floatingStyles,
        open,
        close,
        toggle,
    }
}
